<template>
  <EditDrawer :value="value" v-on="$listeners" :title="Lang.last10Jobs" with-header>
    <template v-slot:header="{ title }">
      <h1 class="header-spacer">{{ title }}</h1>
    </template>

    <div class="content">
      <div v-if="!jobs.length" class="empty">
        {{ Lang.noData || "No jobs yet" }}
      </div>

      <div v-else class="rows">
        <v-card v-for="job in jobs" :key="job.jobId" class="job-row elevation-0">
          <div class="left">
            <div class="title-line">
              <span class="project">{{ job.projectName || job.projectId }}</span>
              <v-chip small :color="statusColor(job.status)" text-color="white" class="text-uppercase ml-2">
                {{ getJobStatusTitle(job.status) || "pending" }}
              </v-chip>
            </div>

            <div class="meta">
              <div class="meta-item">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                <span>{{ formatUSLocalDateTime(job.date) }}</span>
              </div>
            </div>

            <div v-if="job.scenarios && job.scenarios.length" class="scenarios">
              <template v-for="sc in job.scenarios.slice(0, 3)">
                <v-chip :key="sc.scenarioId" small outlined class="mr-1 mb-1">
                  {{ sc.scenarioName || sc.scenarioId }}
                </v-chip>
              </template>
              <v-chip v-if="job.scenarios.length > 3" small outlined class="mb-1">
                +{{ job.scenarios.length - 3 }}
              </v-chip>
            </div>
          </div>

          <div class="right">
            <v-btn text small @click="onOpenClick(job)" :disabled="isOpenDisabled(job)">
              {{ Lang.open || "Open" }}
            </v-btn>
          </div>
        </v-card>
      </div>
    </div>
  </EditDrawer>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";

import EditDrawer from "@/components/drawers/EditDrawer.vue";
import { formatUSLocalDateTime } from "@/helpers/dates";
import Lang from "@/lang";
import ROUTES from "@/settings/routes";
import { LocalJob, LocalJobStatus } from "@/store/modules/localJobs";

type Status = LocalJobStatus;

export default Vue.extend({
  name: "JobsDrawer",
  components: { EditDrawer },

  props: {
    value: { type: Boolean, default: false },
  },

  data() {
    return { Lang, ROUTES };
  },

  computed: {
    ...mapGetters("localJobs", ["all"]),
    jobs(): LocalJob[] {
      const arr = Array.isArray(this.all) ? this.all : [];
      return [...arr].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    },
  },

  methods: {
    formatUSLocalDateTime,
    targetRoute(job: LocalJob) {
      return {
        name: ROUTES.PROJECT.NAME,
        params: { projectId: job.projectId },
        query: { initialTab: "output" },
      };
    },
    isOpenDisabled(job: LocalJob): boolean {
      return job.status !== "COMPLETED";
    },

    async onOpenClick(job: LocalJob) {
      if (!job?.projectId) return;
      if (this.isOpenDisabled(job)) return;

      const target = this.targetRoute(job);

      const sameProject = this.$route.name === target.name && this.$route.params.projectId === job.projectId;

      this.$emit("close");

      if (sameProject) {
        await this.$router.replace({
          ...target,
          query: { ...(this.$route.query || {}), initialTab: "output", reload: Date.now().toString() },
        });
      } else {
        await this.$router.push(target);
      }
    },

    norm(status?: Status) {
      return String(status || "pending").toLowerCase();
    },
    getJobStatusTitle(status?: Status) {
      if (status === "INPROGRESS") {
        return "IN PROGRESS";
      } else {
        return status;
      }
    },
    statusColor(status?: Status) {
      switch (this.norm(status)) {
        case "inprogress":
        case "pending":
        case "submitted":
          return "primary";
        case "completed":
          return "success";
        case "failed":
        case "stopped":
          return "error";
        default:
          return "secondary";
      }
    },
  },
});
</script>

<style scoped lang="scss">
.content {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}
.empty {
  padding: 24px;
  opacity: 0.7;
}
.rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.job-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--v-background-dark-base);
  border: 1px solid var(--v-background-medium-base);
  border-radius: 10px;
  padding: 12px 14px;
}
.left {
  min-width: 0;
  flex: 1;
}
.right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 12px;
}
.title-line {
  display: flex;
  align-items: center;
  gap: 8px;
}
.project {
  font-weight: 600;
  max-width: 520px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  opacity: 0.85;
  font-size: 13px;
}
.meta-item {
  display: inline-flex;
  align-items: center;
}
.meta-sep {
  opacity: 0.5;
}
.scenarios {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
}
.spinning {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.header-spacer {
  margin-right: auto;
}
</style>
